import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'

const SLICE_NAME = 'approvals'

function prepConfig(getState: any) {
	const {
		userLogin: { userInfo },
	} = getState()
	return { headers: { Authorization: `Bearer ${userInfo.token}` } }
}

export interface ReadinessSnapshot {
	ready: boolean
	reasons: string[]
	details?: { siteCount?: number; criterionCount?: number }
}

const initialState = {
	byEvaluation: {} as Record<string, any[]>,
	readinessByEvaluation: {} as Record<string, ReadinessSnapshot>,
	myPending: [] as any[],
	myPendingLoaded: false,
	isLoading: false,
	error: null as string | null,
}

export const fetchApprovals = createAsyncThunk(
	`${SLICE_NAME}/fetch`,
	async (evalId: string, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get(
				`/api/evaluations/${evalId}/approvals`,
				prepConfig(getState),
			)
			return { evalId, approvals: data }
		} catch {
			return rejectWithValue('Failed to load approvals')
		}
	},
)

export const submitApprovals = createAsyncThunk(
	`${SLICE_NAME}/submit`,
	async (
		{ evalId, approvers }: { evalId: string; approvers: string[] },
		{ getState, rejectWithValue, dispatch },
	) => {
		try {
			await axios.post(
				`/api/evaluations/${evalId}/approvals`,
				{ approvers },
				prepConfig(getState),
			)
			const { data: refetched } = await axios.get(
				`/api/evaluations/${evalId}/approvals`,
				prepConfig(getState),
			)
			return { evalId, approvals: refetched }
		} catch {
			return rejectWithValue('Failed to submit approvals')
		}
	},
)

/**
 * Cross-evaluation queue: every approval row where the caller is the
 * *active* approver right now. Backed by D6's GET /api/approvals/my-pending.
 * Returns enriched rows (siteCount + priorApprovedCount) so the global
 * /approvals page can group + display without per-row fetches.
 */
export const fetchMyPendingApprovals = createAsyncThunk(
	`${SLICE_NAME}/fetchMyPending`,
	async (_arg: void, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get(
				'/api/approvals/my-pending',
				prepConfig(getState),
			)
			return data as any[]
		} catch (err: any) {
			const msg =
				err?.response?.data?.message ||
				err?.message ||
				'Failed to load pending approvals'
			return rejectWithValue(msg)
		}
	},
)

/**
 * Submit an already-built chain. Uses D1's POST /approvals/submit endpoint
 * which transitions the evaluation to UNDER_REVIEW and unlocks sequence 1.
 * Distinct from the legacy `submitApprovals` thunk which does a combined
 * build + submit in one call (kept for backward compatibility).
 *
 * Re-fetches both the chain and the evaluation row so byEvaluation +
 * evaluation status reflect the post-submit state.
 */
export const submitChain = createAsyncThunk(
	`${SLICE_NAME}/submitChain`,
	async (evalId: string, { getState, rejectWithValue }) => {
		try {
			await axios.post(
				`/api/evaluations/${evalId}/approvals/submit`,
				{},
				prepConfig(getState),
			)
			const { data: refetched } = await axios.get(
				`/api/evaluations/${evalId}/approvals`,
				prepConfig(getState),
			)
			return { evalId, approvals: refetched }
		} catch (err: any) {
			const msg =
				err?.response?.data?.message || err?.message || 'Failed to submit chain'
			return rejectWithValue(msg)
		}
	},
)

/**
 * Fetch the pre-submit readiness snapshot for an evaluation. Backed by D1's
 * `GET /api/evaluations/:evalId/approvals/readiness`. The response shape is
 * `{ ready, reasons, details }` — the ReadinessChecklist UI maps `reasons`
 * to its 5 check rows.
 */
export const fetchReadiness = createAsyncThunk(
	`${SLICE_NAME}/fetchReadiness`,
	async (evalId: string, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get(
				`/api/evaluations/${evalId}/approvals/readiness`,
				prepConfig(getState),
			)
			return { evalId, snapshot: data as ReadinessSnapshot }
		} catch (err: any) {
			const msg =
				err?.response?.data?.message || err?.message || 'Failed to load readiness'
			return rejectWithValue(msg)
		}
	},
)

/**
 * Save a freshly-edited approval chain — builds the rows in PENDING but does
 * not transition the evaluation status. The companion `submitForApproval`
 * call (D3 / D4) flips status to UNDER_REVIEW once readiness is OK.
 *
 * Body shape matches D1's approvalService.buildChain — array of
 * { userId, sequence?, title? }. Sequence is auto-assigned to array index
 * + 1 when omitted.
 */
export const buildChain = createAsyncThunk(
	`${SLICE_NAME}/buildChain`,
	async (
		{
			evalId,
			approvers,
		}: {
			evalId: string
			approvers: Array<{ userId: string; sequence?: number; title?: string }>
		},
		{ getState, rejectWithValue },
	) => {
		try {
			await axios.post(
				`/api/evaluations/${evalId}/approvals/build`,
				{ approvers },
				prepConfig(getState),
			)
			// Re-fetch via the populated GET so byEvaluation gets full approver
			// objects (name/email/title/etc.) for the chain UI.
			const { data: refetched } = await axios.get(
				`/api/evaluations/${evalId}/approvals`,
				prepConfig(getState),
			)
			return { evalId, approvals: refetched }
		} catch (err: any) {
			const msg =
				err?.response?.data?.message || err?.message || 'Failed to save approval chain'
			return rejectWithValue(msg)
		}
	},
)

export const recordDecision = createAsyncThunk(
	`${SLICE_NAME}/decide`,
	async (
		{
			evalId,
			approvalId,
			decision,
			comments,
			delegateeId,
		}: {
			evalId: string
			approvalId: string
			decision: string
			comments?: string
			/** Required when `decision === 'DELEGATED'`. */
			delegateeId?: string
		},
		{ getState, rejectWithValue },
	) => {
		try {
			await axios.post(
				`/api/evaluations/${evalId}/approvals/${approvalId}/decide`,
				{ decision, comments, delegateeId },
				prepConfig(getState),
			)
			// Refetch the entire chain rather than spot-updating: DELEGATED
			// soft-deletes the original row + creates a new one at the same
			// sequence, and CHANGES_REQUESTED resets every subsequent row to
			// PENDING. A spot update by `_id` can't capture either case
			// cleanly.
			const { data: refetched } = await axios.get(
				`/api/evaluations/${evalId}/approvals`,
				prepConfig(getState),
			)
			return { evalId, approvals: refetched }
		} catch (err: any) {
			const msg =
				err?.response?.data?.message || err?.message || 'Failed to record decision'
			return rejectWithValue(msg)
		}
	},
)

const approvalSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(fetchApprovals.fulfilled, (state, action) => {
				const { evalId, approvals } = action.payload
				state.byEvaluation[evalId] = approvals
			})
			.addCase(submitApprovals.fulfilled, (state, action) => {
				const { evalId, approvals } = action.payload
				state.byEvaluation[evalId] = approvals
			})
			.addCase(buildChain.fulfilled, (state, action) => {
				const { evalId, approvals } = action.payload
				state.byEvaluation[evalId] = approvals
			})
			.addCase(submitChain.fulfilled, (state, action) => {
				const { evalId, approvals } = action.payload
				state.byEvaluation[evalId] = approvals
			})
			.addCase(fetchReadiness.fulfilled, (state, action) => {
				const { evalId, snapshot } = action.payload
				state.readinessByEvaluation[evalId] = snapshot
			})
			.addCase(fetchMyPendingApprovals.fulfilled, (state, action) => {
				state.myPending = action.payload
				state.myPendingLoaded = true
			})
			.addCase(recordDecision.fulfilled, (state, action) => {
				// recordDecision now refetches the whole chain so DELEGATED's new
				// row (different _id, same sequence) and CHANGES_REQUESTED's
				// reset-cascade are both reflected without per-row patching.
				const { evalId, approvals } = action.payload
				state.byEvaluation[evalId] = approvals
			})
	},
})

export default approvalSlice
